import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { unformatCPF } from '../lib/helpers';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  FileSignature,
  PenTool,
  Upload,
  Type,
  CheckCircle,
  Shield,
  Clock,
  User,
  Mail,
  Calendar,
  MapPin,
  Smartphone,
  AlertTriangle,
  Eye,
  Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PublicSignaturePage = () => {
  const { token } = useParams();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  const [signatureData, setSignatureData] = useState(null);
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [error, setError] = useState(null);
  const [showValidation, setShowValidation] = useState(true);
  const [authMode, setAuthMode] = useState('name_cpf');
  const [validationForm, setValidationForm] = useState({
    name: '',
    cpf: '',
    senha: ''
  });
  const [isValidated, setIsValidated] = useState(false);
  const [showDocumentReview, setShowDocumentReview] = useState(false); // Nova tela de revisão
  
  const [signatureMethod, setSignatureMethod] = useState('draw'); // 'draw', 'upload', 'type'
  const [signatureFile, setSignatureFile] = useState(null);
  const [typedSignature, setTypedSignature] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Geolocation
  const [location, setLocation] = useState(null);

  useEffect(() => {
    loadSignatureDocument();
    requestLocation();
  }, [token]);

  useEffect(() => {
    // Se há erro ou documento já foi assinado, não mostrar validação
    if (error || signed) {
      setShowValidation(false);
    }
  }, [error, signed]);

  const requestLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          console.log('Geolocation not available:', error);
          // Não é obrigatório ter localização
        }
      );
    }
  };

  const validateIdentity = async () => {
    try {
      if (!validationForm.cpf.trim() || (authMode === 'cpf_password' ? !validationForm.senha.trim() : !validationForm.name.trim())) {
        toast.error('Preencha todos os campos');
        return;
      }

      const response = await axios.post(`${API}/api/signatures/public/${token}/validate`, {
        name: validationForm.name.trim(),
        cpf: unformatCPF(validationForm.cpf),
        senha: validationForm.senha.trim()
      });

      setSignatureData({
        signer_name: response.data.signer_name,
        signer_cpf: response.data.signer_cpf,
        signer_email: response.data.signer_email,
        signer_phone: response.data.signer_phone,
        created_at: response.data.created_at,
        token_expires_at: response.data.token_expires_at,
      });

      setDocument({
        ...response.data.document,
        title: response.data.document_title,
        signature_requests: response.data.signature_requests
      });

      setIsValidated(true);
      setShowValidation(false);
      setShowDocumentReview(true);
      toast.success('Identidade validada com sucesso!');

    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(authMode === 'cpf_password' ? 'CPF ou senha incorretos.' : 'Nome ou CPF não coincidem com os dados da solicitação.');
      } else {
        toast.error('Erro ao validar identidade. Tente novamente.');
      }
    }
  };

  const proceedToSignature = () => {
    setShowDocumentReview(false);
    // Agora o usuário verá a tela de assinatura
  };

  const loadSignatureDocument = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get(`${API}/api/signatures/public/${token}`);
      
      if (response.data.status === 'signed') {
        setSigned(true);
        return;
      }
      
      setAuthMode(response.data.auth_mode || 'name_cpf');
      setDocument({ title: response.data.document_title });
      
    } catch (error) {
      if (error.response?.status === 401) {
        setError('Token de assinatura expirado ou inválido');
      } else if (error.response?.status === 404) {
        setError('Documento ou solicitação de assinatura não encontrada');
      } else if (error.response?.status === 410) {
        setError('Solicitação de assinatura expirou');
      } else {
        setError(error.response?.data?.detail || 'Erro ao carregar documento para assinatura');
      }
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing functions
  const getCanvasPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches && e.touches.length > 0 ? e.touches[0] : e;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (point.clientX - rect.left) * scaleX,
      y: (point.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    if (e.touches) e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasPoint(e);

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    if (e.touches) e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasPoint(e);

    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const getCanvasSignature = () => {
    const canvas = canvasRef.current;
    return canvas.toDataURL('image/png');
  };

  const handleSignDocument = () => {
    // Validar assinatura baseado no método escolhido
    let isValid = false;
    
    switch (signatureMethod) {
      case 'draw':
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const hasDrawing = imageData.data.some(channel => channel !== 0);
        isValid = hasDrawing;
        break;
      case 'upload':
        isValid = signatureFile !== null;
        break;
      case 'type':
        isValid = typedSignature.trim().length > 2;
        break;
    }
    
    if (!isValid) {
      toast.error('Por favor, forneça sua assinatura antes de continuar');
      return;
    }
    
    setShowConfirmDialog(true);
  };

  const confirmSignature = async () => {
    try {
      setSigning(true);
      
      let signatureData = {
        method: signatureMethod,
        location: location
      };
      
      switch (signatureMethod) {
        case 'draw':
          signatureData.signature_image = getCanvasSignature();
          break;
        case 'upload':
          // Para upload, convertemos para base64
          const reader = new FileReader();
          const base64 = await new Promise((resolve) => {
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(signatureFile);
          });
          signatureData.signature_image = base64;
          break;
        case 'type':
          signatureData.typed_signature = typedSignature;
          break;
      }
      
      await axios.post(`${API}/api/signatures/sign/${token}`, signatureData);
      
      setSigned(true);
      setShowConfirmDialog(false);
      toast.success('Documento assinado com sucesso!');
      
    } catch (error) {
      console.error('Erro ao assinar documento:', error);
      toast.error(error.response?.data?.detail || 'Erro ao assinar documento');
    } finally {
      setSigning(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span>Carregando documento para assinatura...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Erro ao Carregar Documento</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={() => window.location.href = '/'}>
                Voltar ao Início
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">Documento Assinado!</h2>
              <p className="text-gray-600 mb-4">
                Sua assinatura foi registrada com sucesso e possui validade jurídica.
              </p>
              <div className="bg-gray-100 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-gray-600">Documento:</span>
                  <span className="font-medium">{document?.title}</span>
                </div>
              </div>
              <div className="flex items-center justify-center text-sm text-gray-500">
                <Shield className="h-4 w-4 mr-1" />
                <span>Certificado digitalmente</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tela de Validação de Identidade
  if (showValidation && !isValidated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-6 w-6 text-blue-600" />
              <span>Validação de Identidade</span>
            </CardTitle>
            <CardDescription>
              Por segurança, confirme seus dados antes de acessar o documento
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {authMode !== 'cpf_password' && (
              <div className="space-y-2">
                <Label htmlFor="validation_name">Nome Completo *</Label>
                <Input
                  id="validation_name"
                  value={validationForm.name}
                  onChange={(e) => setValidationForm({...validationForm, name: e.target.value})}
                  placeholder="Digite seu nome completo"
                  required
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="validation_cpf">CPF *</Label>
              <Input
                id="validation_cpf"
                value={validationForm.cpf}
                onChange={(e) => setValidationForm({...validationForm, cpf: e.target.value})}
                placeholder="000.000.000-00"
                required
              />
            </div>

            {authMode === 'cpf_password' && (
              <div className="space-y-2">
                <Label htmlFor="validation_senha">Senha única *</Label>
                <Input
                  id="validation_senha"
                  type="password"
                  value={validationForm.senha}
                  onChange={(e) => setValidationForm({...validationForm, senha: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                  placeholder="••••••"
                  required
                />
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Verificação de Segurança</p>
                  <p>{authMode === 'cpf_password' ? 'Informe o CPF e a senha única recebida por e-mail.' : 'Digite exatamente as mesmas informações fornecidas na solicitação de assinatura.'}</p>
                </div>
              </div>
            </div>

            <Button 
              onClick={validateIdentity} 
              className="w-full"
              disabled={!validationForm.cpf.trim() || (authMode === 'cpf_password' ? !validationForm.senha.trim() : !validationForm.name.trim())}
            >
              <Shield className="h-4 w-4 mr-2" />
              Validar Identidade
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tela de Revisão do Documento
  if (showDocumentReview && isValidated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex h-screen">
          {/* Sidebar com informações do signatário */}
          <div className="w-80 bg-white shadow-lg border-r border-gray-200 flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Informações da Assinatura</h2>
              
              {/* Dados do signatário atual */}
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h3 className="font-medium text-blue-900 mb-2">Seus Dados</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-blue-600" />
                    <span>{signatureData?.signer_name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-blue-600" />
                    <span>{signatureData?.signer_email}</span>
                  </div>
                  {signatureData?.signer_phone && (
                    <div className="flex items-center space-x-2">
                      <Smartphone className="h-4 w-4 text-blue-600" />
                      <span>{signatureData?.signer_phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Lista de todos os signatários */}
              <div className="mb-6">
                <h3 className="font-medium text-gray-900 mb-3">Signatários do Documento</h3>
                <div className="space-y-2">
                  {document?.signature_requests?.map((request, index) => (
                    <div 
                      key={index} 
                      className={`flex items-center justify-between p-3 rounded-lg text-sm ${
                        request.signer_email === signatureData?.signer_email 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-600" />
                        <div>
                          <div className="font-medium">{request.signer_name}</div>
                          <div className="text-gray-500">{request.signer_email}</div>
                        </div>
                      </div>
                      <div className={`px-2 py-1 rounded text-xs font-medium ${
                        request.status === 'signed' 
                          ? 'bg-green-100 text-green-800'
                          : request.signer_email === signatureData?.signer_email
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {request.status === 'signed' ? 'Assinado' : 
                         request.signer_email === signatureData?.signer_email ? 'Você' : 'Pendente'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Botão de Assinatura */}
              <Button 
                onClick={proceedToSignature} 
                className="w-full bg-blue-600 hover:bg-blue-700"
                size="lg"
              >
                <FileSignature className="h-5 w-5 mr-2" />
                Assinar Documento
              </Button>
            </div>

            {/* Informações do documento */}
            <div className="p-6 flex-1">
              <h3 className="font-medium text-gray-900 mb-3">Sobre o Documento</h3>
              <div className="space-y-3 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Título:</span>
                  <div className="mt-1">{document?.title}</div>
                </div>
                {document?.description && (
                  <div>
                    <span className="font-medium">Descrição:</span>
                    <div className="mt-1">{document?.description}</div>
                  </div>
                )}
                <div>
                  <span className="font-medium">Solicitado em:</span>
                  <div className="mt-1">{formatDate(signatureData?.created_at)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Área principal com o documento */}
          <div className="flex-1 flex flex-col">
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">
                  Revisão do Documento
                </h1>
                <div className="flex items-center space-x-3">
                  <Button 
                    variant="outline"
                    onClick={() => window.open(`${API}/api/signatures/public/${token}/document/download`, '_blank')}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Baixar PDF
                  </Button>
                </div>
              </div>
              <p className="text-gray-600 mt-2">
                Revise cuidadosamente o documento antes de assinar. Clique em "Assinar Documento" quando estiver pronto.
              </p>
            </div>

            {/* Visualizador do documento */}
            <div className="flex-1 p-4 bg-gray-100">
              <div className="h-full bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <iframe
                  src={`${API}/api/signatures/public/${token}/document/view`}
                  className="w-full h-full"
                  title="Documento para Assinatura"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <FileSignature className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Assinatura Eletrônica</h1>
          </div>
          <p className="text-gray-600">
            Documento com validade jurídica conforme MP 2.200-2/2001
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Document Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Eye className="h-5 w-5" />
                <span>Documento para Assinatura</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{document?.title}</h3>
                {document?.description && (
                  <p className="text-gray-600 mt-1">{document.description}</p>
                )}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t">
                <div className="flex items-center space-x-2 min-w-0">
                  <User className="h-4 w-4 text-gray-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-gray-500">Signatário</p>
                    <p className="font-medium break-words">{signatureData?.signer_name}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 min-w-0">
                  <Mail className="h-4 w-4 text-gray-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium break-all">{signatureData?.signer_email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 min-w-0">
                  <Clock className="h-4 w-4 text-gray-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-gray-500">Solicitado em</p>
                    <p className="font-medium break-words">{formatDate(signatureData?.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 min-w-0">
                  <Calendar className="h-4 w-4 text-gray-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-gray-500">Expira em</p>
                    <p className="font-medium break-words">{formatDate(signatureData?.token_expires_at)}</p>
                  </div>
                </div>
              </div>
              
              {/* Document Preview/Download */}
              <div className="pt-4 border-t">
                <Button variant="outline" className="w-full">
                  <Download className="h-4 w-4 mr-2" />
                  Visualizar Documento
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Signature Interface */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <PenTool className="h-5 w-5" />
                <span>Sua Assinatura</span>
              </CardTitle>
              <CardDescription>
                Escolha como deseja assinar o documento
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={signatureMethod} onValueChange={setSignatureMethod}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="draw">Desenhar</TabsTrigger>
                  <TabsTrigger value="upload">Upload</TabsTrigger>
                  <TabsTrigger value="type">Digitar</TabsTrigger>
                </TabsList>

                {/* Draw Signature */}
                <TabsContent value="draw" className="space-y-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <canvas
                      ref={canvasRef}
                      width={400}
                      height={200}
                      className="w-full border rounded cursor-crosshair touch-none"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      onTouchCancel={stopDrawing}
                    />
                    <div className="flex justify-between mt-2">
                      <p className="text-sm text-gray-500">Desenhe sua assinatura acima</p>
                      <Button variant="outline" size="sm" onClick={clearCanvas}>
                        Limpar
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                {/* Upload Signature */}
                <TabsContent value="upload" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signature-file">Arquivo da Assinatura</Label>
                    <Input
                      id="signature-file"
                      type="file"
                      accept="image/*"
                      onChange={(e) => setSignatureFile(e.target.files[0])}
                    />
                    <p className="text-sm text-gray-500">
                      Envie uma imagem da sua assinatura (PNG, JPG)
                    </p>
                  </div>
                  {signatureFile && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-700 mb-2">Preview:</p>
                      <img
                        src={URL.createObjectURL(signatureFile)}
                        alt="Preview da assinatura"
                        className="max-w-full h-32 border rounded"
                      />
                    </div>
                  )}
                </TabsContent>

                {/* Type Signature */}
                <TabsContent value="type" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="typed-signature">Digite sua Assinatura</Label>
                    <Input
                      id="typed-signature"
                      value={typedSignature}
                      onChange={(e) => setTypedSignature(e.target.value)}
                      placeholder="Digite seu nome completo"
                      className="text-2xl font-serif"
                    />
                    <p className="text-sm text-gray-500">
                      Será exibido em fonte cursiva elegante
                    </p>
                  </div>
                  {typedSignature && (
                    <div className="mt-4 p-4 border rounded bg-gray-50">
                      <p className="text-sm text-gray-700 mb-2">Preview:</p>
                      <p className="text-3xl font-serif italic">{typedSignature}</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              {/* Legal Info */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start space-x-2">
                  <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Validade Jurídica</p>
                    <p className="text-sm text-blue-700">
                      Esta assinatura terá validade jurídica conforme a MP 2.200-2/2001 e será 
                      certificada digitalmente com timestamp, IP e localização.
                    </p>
                  </div>
                </div>
              </div>

              {/* Sign Button */}
              <Button 
                onClick={handleSignDocument}
                className="w-full mt-6"
                size="lg"
              >
                <FileSignature className="h-5 w-5 mr-2" />
                Assinar Documento
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Confirmation Dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Assinatura</DialogTitle>
              <DialogDescription>
                Você está prestes a assinar digitalmente o documento "{document?.title}". 
                Esta ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium mb-2">Informações da Assinatura:</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Signatário:</span>
                    <span className="font-medium">{signatureData?.signer_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Email:</span>
                    <span className="font-medium">{signatureData?.signer_email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Método:</span>
                    <span className="font-medium">
                      {signatureMethod === 'draw' && 'Assinatura Desenhada'}
                      {signatureMethod === 'upload' && 'Imagem Carregada'}
                      {signatureMethod === 'type' && 'Assinatura Digitada'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Data/Hora:</span>
                    <span className="font-medium">{new Date().toLocaleString('pt-BR')}</span>
                  </div>
                  {location && (
                    <div className="flex justify-between">
                      <span>Localização:</span>
                      <span className="font-medium">
                        <MapPin className="h-3 w-3 inline mr-1" />
                        Capturada
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Shield className="h-4 w-4" />
                <span>
                  Ao confirmar, você declara estar ciente do conteúdo do documento e 
                  concorda com seus termos.
                </span>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={confirmSignature} disabled={signing}>
                {signing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Assinando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Confirmar Assinatura
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PublicSignaturePage;
